import { getFormInitialValues, optionalFormField } from 'utils/methods/helpers';
import { findARole } from 'utils/helper/product';
import { getMultiRoleField, USER_FIELDS } from 'utils/formFields/user';
import { checkIfUserIsSimpleUser } from 'utils/helper/roles';
import {
  validateName,
  validatePassCode,
  validatePhoneNumber,
  validateRequired,
  validateString,
  validationObject,
  validateEmail,
} from 'utils/validations';

export const initialValues = data => {
  return getFormInitialValues(data);
};
export const departmentFilter = transformedDepartmentList => {
  return optionalFormField({
    showWhen: transformedDepartmentList.length > 0,
    field: [
      {
        label: 'departments',
        field: 'departmentId',
        type: 'select',
        options: [
          ...transformedDepartmentList,
          { id: 'EMPTY_VALUE', name: 'No department' },
        ],
      },
    ],
  });
};
const baseValidation = {
  firstName: validateName(30),
  lastName: validateName(30),
  role: validateRequired(),
};

export const validationSchema = (role, editMode) => {
  if (checkIfUserIsSimpleUser(role)) {
    return validationObject({
      ...baseValidation,
      employeeId: validateString(30, true),
      passcode: validatePassCode(!editMode, 4, 6),
      phone: validatePhoneNumber(),
    });
  } else {
    return validationObject({
      ...baseValidation,
      employeeId: validateString(30, false),
      email: validateEmail() && validateRequired(),
      phone: validatePhoneNumber(),
    });
  }
};

export const initialState = {
  employeeId: '',
  role: '', //SRM-1769
  firstName: '',
  lastName: '',
  patrolService: [],
  transportService: [],
  experiences: [],
  languages: ['EN'],
  qualifications: [],
  identifications: [],
  archived: false,
  emailVerified: false,
  phoneVerified: false,
  notes: '',
  phone: undefined,
  email: undefined,
  isResponder: false,
  isTeamLead: false,
};

const showIsTeamLeadOrResponder = (role, serviceRoleMap) => {
  const selectedRole = findARole(role);

  if (!selectedRole) return [];

  if (selectedRole.canBeTeamLead) {
    return [USER_FIELDS.isTeamLead];
  }

  if (selectedRole.multiRole) {
    const multiRoleFields = [selectedRole].reduce((acc, cv) => {
      const fields = getMultiRoleField({
        role: cv.id,
        serviceRoleMap,
      });
      return fields ? [...acc, ...fields] : acc;
    }, []);

    return multiRoleFields.length < 1 ? [USER_FIELDS.hidden] : multiRoleFields;
  }

  return [];
};

const baseGeneralSection = ({ availableRoles, roleDisabled }) => {
  return [
    USER_FIELDS.role({
      availableRoles,
      disabled: roleDisabled,
    }),
    USER_FIELDS.firstName,
    USER_FIELDS.lastName,
  ];
};

export const generalSection = ({
  countries,
  editMode,
  role,
  // email,
  availableRoles,
  userAssignedDepartments = [],
  selectedDepartments,
  securityCompanyId,
}) => {
  const defaultGeneralSection = baseGeneralSection({
    availableRoles,
    roleDisabled: editMode,
    /**
     *TODO: make sure that the role gets disabled based on the type of user-> email or non-email
     */
    // if the user falls under email then it can only change roles with email users and if user role is non-email with it can change with only non email users
  });
  const showDepartments = userAssignedDepartments.length > 0;

  if (checkIfUserIsSimpleUser(role)) {
    return [
      USER_FIELDS.employeeId({
        role: role,
        size: 'half',
      }),
      ...defaultGeneralSection,
      ...optionalFormField({
        showWhen: showDepartments,
        field: USER_FIELDS.departments({
          userAssignedDepartments,
          updateDependentField: [
            // if this component is updated, always clear assignedSite
            {
              key: 'assignedSite',
              passValue: '', // assign this value
              condition: () => true, // always true, therefore, it will ensure that assignedSite will be deselected when departments is changed
            },
          ],
        }),
      }),
      USER_FIELDS.passcode(editMode),
      USER_FIELDS.phone(countries),
      USER_FIELDS.assignedSite({
        selectedDepartments,
        securityCompanyId,
        userAssignedDepartments,
      }),
    ];
  } else {
    return [
      USER_FIELDS.employeeId({ size: 'half' }),
      ...defaultGeneralSection,
      ...optionalFormField({
        showWhen: showDepartments,
        field: USER_FIELDS.departments({ userAssignedDepartments }),
      }),
      USER_FIELDS.email(editMode),
      USER_FIELDS.phone(countries),
      USER_FIELDS.assignedSite({
        selectedDepartments,
        securityCompanyId,
        userAssignedDepartments,
      }),
    ];
  }
};

export const detailedSection = ({
  languages,
  role,
  availableServices,
  serviceRoleMap,
  allowableCategories,
}) => {
  return [
    ...showIsTeamLeadOrResponder(role, serviceRoleMap),
    ...USER_FIELDS.services({
      services: availableServices,
      role,
    }),
    USER_FIELDS.qualifications(allowableCategories),
    USER_FIELDS.languages(languages),
    USER_FIELDS.notes,
  ];
};
